import { ChatMarkdown } from "@/core/components/chat-markdown";
import { Stack } from "@/core/components/ui/stack";
import { EditorResult } from "@/payroll/editor/editor-result";
import type { EnrichedMessage } from "@/payroll/editor/types";
import { OwnerMessage } from "./owner-message";
import { TurnFailure } from "./turn-failure";
export const TranscriptMessage = ({
  message,
  disabled,
  onReply,
  onWriteOwn,
  onResend,
  /** The request this answer belongs to, so a failed answer can be sent again. */
  request,
  /** Whether anything can be sent at all. Not whether this is the newest. */
  canResend,
}: {
  message: EnrichedMessage;
  disabled: boolean;
  onReply: (value: string) => void;
  onWriteOwn: () => void;
  onResend: (request: string) => void;
  request: string;
  canResend: boolean;
}) => (
  <div className="min-w-0 break-words">
    {message.result ? (
      <EditorResult
        result={message.result}
        onReply={onReply}
        onWriteOwn={onWriteOwn}
        disabled={disabled}
      />
    ) : (
      message.role !== "tool" &&
      // The customer's own words read as a sent message; the
      // assistant answers in the body of the page, the way every chat
      // the owner already uses behaves.
      (message.role === "user" ? (
        <>
          <OwnerMessage>{message.content}</OwnerMessage>
          {/* Outside the bubble. The bubble is what they said; this is what
              became of it, and putting the second inside the first made a
              failure look like part of their own words. */}
          {message.status === "failed" && (
            <TurnFailure
              className="ml-auto max-w-[85%]"
              note="This request stopped. Review the saved proposal before you send a new one."
              request={message.content}
              detail="A payroll rules request stopped before it finished, and nothing was saved."
              onResend={onResend}
              disabled={!canResend}
            />
          )}
        </>
      ) : (
        <Stack
          gap="none"
          align="stretch"
          direction="column"
          className="flex flex-col gap-1 text-sm text-secondary-text"
        >
          {message.role === "assistant" && !message.content.trim() ? (
            <TurnFailure
              note="The assistant returned no reply. Review the proposal before you send another request."
              request={request}
              detail="A payroll rules request finished with no reply at all."
              onResend={onResend}
              disabled={!canResend}
            />
          ) : (
            <>
              <ChatMarkdown>{message.content}</ChatMarkdown>
              {/* The reply already asks for the request again, so the button
                  belongs under it. The server marks this ending; the wording
                  is not what decides. */}
              {message.retryable && (
                <TurnFailure
                  request={request}
                  detail="A payroll rules request ran out of steps before it could save anything."
                  onResend={onResend}
                  disabled={!canResend}
                />
              )}
            </>
          )}
        </Stack>
      ))
    )}
  </div>
);
